import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp, TrendingDown, AlertTriangle, Lightbulb, ShieldCheck,
  ArrowUpRight, ArrowDownRight, Brain, Zap, DollarSign, BarChart3
} from "lucide-react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useExpenses } from "@/contexts/ExpenseContext";
import { computeFinancialMetrics } from "@/lib/accounting/financialEngine";

export type InsightType = "revenue" | "expense" | "cashflow" | "profitability" | "tax";
export type InsightSeverity = "positive" | "warning" | "critical" | "info";

export interface FinancialInsight {
  id: string;
  type: InsightType;
  title: string;
  description: string;
  severity: InsightSeverity;
  metric?: string;
  change?: string;
  recommendation?: string;
}

const SEVERITY_STYLES: Record<InsightSeverity, { bg: string; border: string; icon: string; badge: string }> = {
  positive: { bg: "bg-success/5", border: "border-success/20", icon: "text-success", badge: "bg-success/10 text-success" },
  warning: { bg: "bg-warning/5", border: "border-warning/20", icon: "text-warning", badge: "bg-warning/10 text-warning" },
  critical: { bg: "bg-destructive/5", border: "border-destructive/20", icon: "text-destructive", badge: "bg-destructive/10 text-destructive" },
  info: { bg: "bg-accent/5", border: "border-accent/20", icon: "text-accent", badge: "bg-accent/10 text-accent" },
};

const TYPE_ICONS: Record<InsightType, React.ComponentType<{ className?: string }>> = {
  revenue: TrendingUp,
  expense: BarChart3,
  cashflow: DollarSign,
  profitability: Zap,
  tax: ShieldCheck,
};

const TYPE_LABELS: Record<InsightType, string> = {
  revenue: "Revenue",
  expense: "Expenses",
  cashflow: "Cash Flow",
  profitability: "Profitability",
  tax: "Tax Optimization",
};

function generateInsights(
  vouchers: ReturnType<typeof useAccounting>["vouchers"],
  ledgers: ReturnType<typeof useAccounting>["ledgers"],
  stockItems: ReturnType<typeof useAccounting>["stockItems"],
  expenses: ReturnType<typeof useExpenses>["expenses"] = []
): FinancialInsight[] {
  const approved = vouchers.filter((v) => v.status === "Approved");

  // Empty workspace — no transactions
  if (approved.length === 0 && expenses.length === 0) {
    return [];
  }

  const metrics = computeFinancialMetrics({
    vouchers,
    ledgers,
    stockItems,
    expenses,
  });

  const lowStockItems = stockItems.filter((si) => si.reorderLevel && si.currentQty <= si.reorderLevel);
  const debtors = ledgers.filter((l) => l.group === "Sundry Debtors");
  const creditors = ledgers.filter((l) => l.group === "Sundry Creditors");

  const insights: FinancialInsight[] = [];

  // Revenue insights
  if (metrics.netSalesRevenue > 0) {
    insights.push({
      id: "rev-1",
      type: "revenue",
      title: "Strong Top-Line Revenue",
      description: `Net taxable revenue stands at ₹${metrics.netSalesRevenue.toLocaleString("en-IN")} across ${metrics.salesVouchersCount} sales transactions (Gross Invoiced: ₹${metrics.grossSales.toLocaleString("en-IN")}). Strong customer demand recognized.`,
      severity: "positive",
      metric: `₹${metrics.netSalesRevenue.toLocaleString("en-IN")}`,
      change: `${metrics.salesVouchersCount} Invoices`,
      recommendation: "Maintain customer relationship momentum and follow up on credit settlements",
    });
  }

  // Operating Expense trends
  insights.push({
    id: "exp-1",
    type: "expense",
    title: metrics.operatingExpenseRatio > 50 ? "Operating Overheads Require Attention" : "Operating Expenses Well Controlled",
    description: `Operating expenses are ₹${metrics.operatingExpenses.toLocaleString("en-IN")} (${metrics.operatingExpenseRatio}% of net revenue). Purchases of goods (₹${metrics.taxablePurchases.toLocaleString("en-IN")}) are properly capitalized to Stock-in-Hand.`,
    severity: metrics.operatingExpenseRatio > 50 ? "warning" : "positive",
    metric: `${metrics.operatingExpenseRatio}%`,
    change: `₹${metrics.operatingExpenses.toLocaleString("en-IN")}`,
    recommendation: metrics.operatingExpenseRatio > 50 ? "Audit operational overheads and renegotiate utility/service contracts" : "Maintain current operational cost discipline",
  });

  // Cash flow
  insights.push({
    id: "cf-1",
    type: "cashflow",
    title: metrics.netCashFlow >= 0 ? "Positive Cash Flow" : "Negative Cash Flow Warning",
    description: metrics.netCashFlow >= 0
      ? `Net cash surplus of ₹${metrics.netCashFlow.toLocaleString("en-IN")}. Total collections (₹${metrics.totalReceipts.toLocaleString("en-IN")}) exceed disbursements (₹${metrics.totalPayments.toLocaleString("en-IN")}).`
      : `Disbursements exceed collections by ₹${Math.abs(metrics.netCashFlow).toLocaleString("en-IN")}. Immediate collection acceleration recommended.`,
    severity: metrics.netCashFlow > 0 ? "positive" : metrics.netCashFlow === 0 ? "info" : "critical",
    metric: `₹${Math.abs(metrics.netCashFlow).toLocaleString("en-IN")}`,
    change: metrics.netCashFlow >= 0 ? "Surplus" : "Deficit",
    recommendation: metrics.netCashFlow < 0 ? "Accelerate debtor collections and defer non-critical payments" : "Consider short-term yield on surplus liquidity",
  });

  if (metrics.totalReceivable > 0) {
    const receivableDays = metrics.netSalesRevenue > 0 ? Math.round((metrics.totalReceivable / metrics.netSalesRevenue) * 365) : 0;
    insights.push({
      id: "cf-2",
      type: "cashflow",
      title: receivableDays > 45 ? "High Outstanding Receivables" : "Receivables Under Control",
      description: `₹${metrics.totalReceivable.toLocaleString("en-IN")} outstanding from ${debtors.length} parties (~${receivableDays} days of sales).`,
      severity: receivableDays > 60 ? "critical" : receivableDays > 45 ? "warning" : "positive",
      metric: `${receivableDays} days`,
      recommendation: receivableDays > 45 ? "Implement early payment discounts and stricter credit terms" : "Maintain current collection practices",
    });
  }

  // Profitability
  insights.push({
    id: "prof-1",
    type: "profitability",
    title: metrics.netProfitMargin > 20 ? "Strong Profit Margins" : metrics.netProfitMargin > 0 ? "Profitable Operations" : "Operating at Deficit",
    description: `Net profit is ₹${metrics.netProfit.toLocaleString("en-IN")} (${metrics.netProfitMargin}% margin) with gross profit at ₹${metrics.grossProfit.toLocaleString("en-IN")} (${metrics.grossProfitMargin}% margin after ₹${metrics.cogs.toLocaleString("en-IN")} COGS).`,
    severity: metrics.netProfitMargin > 20 ? "positive" : metrics.netProfitMargin > 0 ? "info" : "critical",
    metric: `${metrics.netProfitMargin}%`,
    change: `₹${metrics.netProfit.toLocaleString("en-IN")}`,
    recommendation: metrics.netProfitMargin < 15 ? "Review pricing strategy and product margins" : "Reinvest healthy profits into high-demand stock lines",
  });

  // GST Compliance & ITC
  if (metrics.gstStatusType === "credit") {
    insights.push({
      id: "tax-1",
      type: "tax",
      title: "Excess ITC Carried Forward",
      description: `You have ₹${metrics.excessITC.toLocaleString("en-IN")} of Excess Input Tax Credit (ITC: ₹${metrics.gstInput.toLocaleString("en-IN")} vs Output: ₹${metrics.gstOutput.toLocaleString("en-IN")}). Your net tax payable in cash is ₹0.00.`,
      severity: "positive",
      metric: `₹${metrics.excessITC.toLocaleString("en-IN")}`,
      recommendation: "Ensure supplier returns are reconciled in GSTR-2B before monthly GSTR-3B submission",
    });
  } else if (metrics.gstStatusType === "payable") {
    insights.push({
      id: "tax-1",
      type: "tax",
      title: "Net GST Liability Due",
      description: `Net tax payable: ₹${metrics.netGstPayable.toLocaleString("en-IN")} (Output: ₹${metrics.gstOutput.toLocaleString("en-IN")} minus ITC: ₹${metrics.gstInput.toLocaleString("en-IN")}). File GSTR-3B by the 20th to avoid interest.`,
      severity: "warning",
      metric: `₹${metrics.netGstPayable.toLocaleString("en-IN")}`,
      recommendation: "Verify all purchase bills are claimed before discharging cash liability",
    });
  }

  if (lowStockItems.length > 0) {
    insights.push({
      id: "inv-1",
      type: "expense",
      title: `${lowStockItems.length} Items Below Reorder Level`,
      description: `Low stock on: ${lowStockItems.slice(0, 3).map((i) => i.name).join(", ")}${lowStockItems.length > 3 ? ` and ${lowStockItems.length - 3} more` : ""}. Stockouts can lead to lost sales.`,
      severity: "warning",
      metric: `${lowStockItems.length} items`,
      recommendation: "Create purchase orders for low-stock products to replenish warehouse inventory",
    });
  }

  return insights;
}

export function useFinancialInsights() {
  const { vouchers, ledgers, stockItems } = useAccounting();
  const { expenses } = useExpenses();
  return useMemo(() => generateInsights(vouchers, ledgers, stockItems, expenses), [vouchers, ledgers, stockItems, expenses]);
}

// Compact card for dashboard embedding
export function InsightCard({ insight, index = 0 }: { insight: FinancialInsight; index?: number }) {
  const style = SEVERITY_STYLES[insight.severity];
  const Icon = TYPE_ICONS[insight.type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`p-4 rounded-lg border ${style.bg} ${style.border}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg ${style.badge} flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-wider font-medium ${style.badge} px-1.5 py-0.5 rounded`}>
              {TYPE_LABELS[insight.type]}
            </span>
            {insight.change && (
              <span className={`text-xs font-medium ${insight.severity === "positive" ? "text-success" : insight.severity === "critical" ? "text-destructive" : "text-muted-foreground"}`}>
                {insight.change}
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-foreground leading-tight">{insight.title}</h4>
          {insight.metric && (
            <div className="text-lg font-bold text-foreground mt-1">{insight.metric}</div>
          )}
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{insight.description}</p>
          {insight.recommendation && (
            <div className="flex items-start gap-1.5 mt-2 p-2 rounded bg-card/50 border border-border/50">
              <Lightbulb className="h-3 w-3 text-accent mt-0.5 flex-shrink-0" />
              <span className="text-[11px] text-muted-foreground leading-snug">{insight.recommendation}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Dashboard summary strip (top 3 insights)
export function InsightsSummaryStrip() {
  const insights = useFinancialInsights();
  const topInsights = insights
    .sort((a, b) => {
      const order: Record<InsightSeverity, number> = { critical: 0, warning: 1, positive: 2, info: 3 };
      return order[a.severity] - order[b.severity];
    })
    .slice(0, 3);

  if (topInsights.length === 0) return null;

  return (
    <div className="p-4 rounded-lg bg-card border border-border shadow-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
          <Brain className="h-3.5 w-3.5 text-accent" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">AI Financial Insights</h3>
        <span className="text-[10px] uppercase tracking-wider text-accent font-medium bg-accent/10 px-1.5 py-0.5 rounded">Smart Analysis</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {topInsights.map((insight, i) => {
          const style = SEVERITY_STYLES[insight.severity];
          const Icon = TYPE_ICONS[insight.type];
          return (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
              className={`flex items-start gap-2.5 p-3 rounded-lg ${style.bg} ${style.border} border`}
            >
              <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${style.icon}`} />
              <div>
                <div className="text-xs font-semibold text-foreground">{insight.title}</div>
                {insight.metric && <div className="text-sm font-bold text-foreground mt-0.5">{insight.metric}</div>}
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">{insight.description}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
